import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Search,
  Filter,
  Image,
  Film,
  Music,
  FileText,
  File,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/utils/api';
import clsx from 'clsx';
import type { FileInfo } from '@/types';

const FILE_TYPE_ICONS = {
  image: Image,
  video: Film,
  audio: Music,
  pdf: FileText,
  file: File
};

const FILE_TYPE_COLORS = {
  image: 'text-green-600 bg-green-50',
  video: 'text-purple-600 bg-purple-50',
  audio: 'text-orange-600 bg-orange-50',
  pdf: 'text-red-600 bg-red-50',
  file: 'text-gray-600 bg-gray-50'
};

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export default function FilesPage() {
  const navigate = useNavigate();

  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [probing, setProbing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data: files = [], isLoading, refetch } = useQuery({
    queryKey: ['files'],
    queryFn: () => api.listFiles(false)
  });

  const probeMutation = useMutation({
    mutationFn: (urls: string[]) => api.probeFileSizes(urls),
    onSuccess: () => {
      setProbing(false);
    }
  });

  const filteredFiles = files.filter((file) => {
    if (filterType !== 'all' && file.type !== filterType) return false;
    if (searchQuery && !file.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const toggleFile = (fileId: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const toggleAll = () => {
    if (selectedFiles.size === filteredFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
    }
  };

  const handleProbe = () => {
    if (selectedFiles.size === 0) return;
    setProbing(true);
    const urls = files.filter(f => selectedFiles.has(f.id)).map(f => f.url);
    probeMutation.mutate(urls);
  };

  const handleDownload = async (file: FileInfo) => {
    try {
      const blob = await api.downloadFile(file.url, file.name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('下载失败，请重试');
    }
  };

  const handleBatchDownload = async () => {
    setDownloading(true);
    const filesToDownload = files.filter(f => selectedFiles.has(f.id));

    for (const file of filesToDownload) {
      await handleDownload(file);
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setDownloading(false);
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">文件列表</h1>
        </div>
        <button
          onClick={() => navigate('/upload')}
          className="btn btn-primary flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          上传文件
        </button>
      </div>

      <div className="card p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索文件..."
              className="input pl-10"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input py-2 w-auto"
            >
              <option value="all">全部类型</option>
              <option value="image">图片</option>
              <option value="video">视频</option>
              <option value="audio">音频</option>
              <option value="pdf">PDF</option>
              <option value="file">其他文件</option>
            </select>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => refetch()}
              className="btn btn-ghost flex items-center gap-2"
              disabled={isLoading}
            >
              <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
              刷新
            </button>

            {selectedFiles.size > 0 && (
              <>
                <button
                  onClick={handleProbe}
                  className="btn btn-secondary flex items-center gap-2"
                  disabled={probing}
                >
                  {probing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  探测大小
                </button>
                <button
                  onClick={handleBatchDownload}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={downloading}
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  下载 ({selectedFiles.size})
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-12 text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 text-primary-600 animate-spin" />
          <p className="text-gray-500">加载文件中...</p>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="card p-12 text-center">
          <File className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">
            {files.length === 0 ? '该页面暂无文件' : '没有匹配的文件'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_100px_120px_100px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedFiles.size === filteredFiles.length}
                onChange={toggleAll}
                className="w-4 h-4 rounded border-gray-300"
              />
            </div>
            <div>文件名</div>
            <div>类型</div>
            <div>大小</div>
            <div>操作</div>
          </div>

          <div className="divide-y divide-gray-100">
            {filteredFiles.map((file) => {
              const Icon = FILE_TYPE_ICONS[file.type] || File;
              const colorClass = FILE_TYPE_COLORS[file.type] || FILE_TYPE_COLORS.file;
              const isSelected = selectedFiles.has(file.id);

              return (
                <div
                  key={file.id}
                  className={clsx(
                    'grid grid-cols-[auto_1fr_100px_120px_100px] gap-4 px-4 py-3 items-center hover:bg-gray-50 transition-colors',
                    isSelected && 'bg-primary-50'
                  )}
                >
                  <div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleFile(file.id)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                  </div>

                  <div className="flex items-center gap-3 min-w-0">
                    <div className={clsx('p-2 rounded-lg shrink-0', colorClass)}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{file.name}</p>
                      {file.caption && (
                        <p className="text-xs text-gray-500 truncate">{file.caption}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <span className={clsx('text-sm capitalize', colorClass)}>
                      {file.type}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600">
                    {formatFileSize(file.size)}
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDownload(file)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="下载"
                    >
                      <Download className="w-4 h-4 text-gray-600" />
                    </button>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="在新窗口打开"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-600" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filteredFiles.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          共 {filteredFiles.length} 个文件，已选择 {selectedFiles.size} 个
        </div>
      )}
    </div>
  );
}